import React, { useState, useEffect, useRef } from "react";
import { user } from "server/AccrossTypes";

interface MultiSelectProps {
  title: string;
  selectedItems: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  placeholder?: string;
  isEditable?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  title,
  selectedItems,
  onSelectionChange,
  placeholder = "Sélectionnez des étudiants...",
  isEditable = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [students, setStudents] = useState<user[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchStudents = async () => {
      console.log("MultiSelect: Début du chargement des étudiants");
      setLoading(true);
      try {
        const { getStudentsAll } = await import(
          "server/Students CRUD/Requests"
        );
        console.log("MultiSelect: Import de getStudentsAll réussi");

        const response = await getStudentsAll();
        console.log("MultiSelect: Réponse de l'API:", response);

        if (response.status === 200 && response.body) {
          console.log("MultiSelect: Body de la réponse:", response.body);
          console.log("MultiSelect: Type du body:", typeof response.body);

          // La réponse est directement un tableau d'étudiants
          if (Array.isArray(response.body)) {
            console.log(
              "MultiSelect: Nombre d'étudiants reçus:",
              response.body.length,
            );
            if (response.body.length > 0) {
              console.log("MultiSelect: Premier étudiant:", response.body[0]);
              console.log("MultiSelect: Structure du premier étudiant:", {
                _id: response.body[0]._id,
                firstname: response.body[0].firstname,
                lastname: response.body[0].lastname,
                matricule: response.body[0].matricule,
              });
            }
            setStudents(response.body);
          } else {
            console.log(
              "MultiSelect: Le body n'est pas un tableau:",
              response.body,
            );
          }
        } else {
          console.log("MultiSelect: Réponse non valide:", response);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des étudiants:", error);
      } finally {
        setLoading(false);
        console.log("MultiSelect: Chargement terminé");
      }
    };

    fetchStudents();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredStudents = students.filter((student) =>
    `${student.firstname} ${student.lastname} ${student.matricule || ""}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  const handleToggleItem = (studentId: string) => {
    const newSelection = selectedItems.includes(studentId)
      ? selectedItems.filter((id) => id !== studentId)
      : [...selectedItems, studentId];
    onSelectionChange(newSelection);
  };

  const handleRemoveStudent = (studentId: string) => {
    const newSelection = selectedItems.filter((id) => id !== studentId);
    onSelectionChange(newSelection);
  };

  const getSelectedStudents = () => {
    return students.filter((student) => selectedItems.includes(student._id));
  };

  const selectedStudents = getSelectedStudents();

  if (!isEditable) {
    return (
      <div className="flex flex-col items-center justify-center mb-4 w-full">
        <label className="block mb-2 w-full">{title}</label>
        <div className="p-2 w-full border border-[#EDEDED] rounded bg-[#EDEDED] text-6x1 text-[#858585] min-h-[40px]">
          {selectedStudents.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selectedStudents.map((student) => (
                <span
                  key={student._id}
                  className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm"
                >
                  {student.firstname} {student.lastname}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-gray-500">Aucun enfant sélectionné</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center mb-4 w-full">
      <label className="block mb-2 w-full text-sm font-medium text-gray-700">
        {title}
      </label>
      <div className="relative w-full" ref={dropdownRef}>
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (!isOpen) {
                setIsOpen(true);
              }
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={
              selectedStudents.length > 0 ? "Rechercher..." : placeholder
            }
            className="p-3 w-full border border-[#EDEDED] rounded-lg bg-[#EDEDED] text-sm text-[#858585] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
          />

          {/* Affichage des étudiants sélectionnés */}
          {selectedStudents.length > 0 && (
            <div className="mt-2">
              <div className="flex flex-wrap gap-2">
                {selectedStudents.map((student) => (
                  <div
                    key={student._id}
                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2 group hover:bg-blue-200 transition-colors duration-200"
                  >
                    <span className="truncate max-w-[120px] sm:max-w-[150px] md:max-w-[200px]">
                      {student.firstname} {student.lastname}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveStudent(student._id)}
                      className="text-blue-600 hover:text-blue-800 font-bold text-lg leading-none opacity-60 hover:opacity-100 transition-opacity duration-200"
                      title="Supprimer"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                Chargement des étudiants...
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {searchTerm
                  ? "Aucun étudiant trouvé"
                  : "Aucun étudiant disponible"}
              </div>
            ) : (
              filteredStudents.map((student) => (
                <div
                  key={student._id}
                  className={`p-3 cursor-pointer hover:bg-gray-50 flex items-center transition-colors duration-150 ${
                    selectedItems.includes(student._id)
                      ? "bg-blue-50 border-l-4 border-blue-500"
                      : ""
                  }`}
                  onClick={() => {
                    handleToggleItem(student._id);
                    setSearchTerm(""); // Vider le champ après sélection
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(student._id)}
                    onChange={() => {}} // Géré par onClick du parent
                    className="mr-3 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {student.firstname} {student.lastname}
                    </div>
                    {student.matricule && (
                      <div className="text-sm text-gray-500 truncate">
                        Matricule: {student.matricule}
                      </div>
                    )}
                  </div>
                  {selectedItems.includes(student._id) && (
                    <div className="ml-2 text-blue-600">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;
